import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import {
  Button,
  ErrorState,
  InfoHint,
  Skeleton,
  StatusPill,
  toast,
} from '../../components/ui';
import { cn } from '../../lib/cn';
import { formatDate, formatHours, formatMoney } from '../../lib/format';
import {
  useManagementFeePolicy,
  useReverseManagementFee,
  useSetClientManagementFee,
  useSupportBank,
  type HourBlock,
  type HourLedgerRow,
  type SupportBankDetail,
} from './api';
import { UnbankedSales } from './UnbankedSales';
import { UnpaidBankHours } from './UnpaidBankHours';
import { relativeDays, thresholdLabel } from './components';

const BLOCK_PILL: Record<HourBlock['status'], { status: 'success' | 'warning' | 'error' | 'neutral' | 'draft'; label: string }> = {
  ACTIVE: { status: 'success', label: 'Active' },
  // An invoice block is pending while its invoice is a draft; a hand-added
  // block while it is marked unpaid. Hours on a SENT invoice are Active.
  PENDING: { status: 'draft', label: 'Not active yet' },
  EXHAUSTED: { status: 'neutral', label: 'Used up' },
  EXPIRED: { status: 'warning', label: 'Expired' },
  REFUNDED: { status: 'neutral', label: 'Refunded' },
};

const REASON_LABEL: Record<HourLedgerRow['reason'], string> = {
  purchase: 'Purchased',
  consumption: 'Support time',
  overage: 'Beyond the bank',
  expiry: 'Expired unused',
  refund: 'Refunded',
  adjustment: 'Adjustment',
  // Named for what it is, on the statement the client is shown.
  management_fee: 'Minimum management fee',
  management_fee_reversal: 'Management fee reversed',
};

/**
 * The per-client override, on the client. A LABEL, not a lesson: the amount,
 * the exemption, and what it has cost so far — the reasoning is behind the (i).
 */
function ManagementFeeRow({ bank }: { bank: SupportBankDetail }) {
  const policy = useManagementFeePolicy();
  const save = useSetClientManagementFee();
  const rule = bank.managementFee;
  const [hours, setHours] = useState(String(rule.hours));
  const [exempt, setExempt] = useState(rule.exempt);

  useEffect(() => {
    setHours(String(rule.hours));
    setExempt(rule.exempt);
  }, [rule.hours, rule.exempt]);

  const parsed = Number(hours);
  const valid = Number.isFinite(parsed) && parsed >= 0 && parsed <= 40;
  const dirty = exempt !== rule.exempt || (valid && parsed !== rule.hours);
  const orgDefault = policy.data?.hours ?? null;

  const commit = async () => {
    if (!dirty || !valid) return;
    try {
      await save.mutateAsync({
        clientId: bank.clientId,
        hours: exempt ? undefined : parsed,
        exempt,
      });
      toast.success(
        exempt
          ? `${bank.clientName} is exempt from the management fee.`
          : `Management fee set to ${formatHours(parsed, { unit: true })} a month.`,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'That did not save.');
    }
  };

  return (
    <div>
      <p className="mb-2 text-caption font-bold uppercase tracking-wider text-grey-500">
        Minimum management fee
        <InfoHint label="Minimum management fee" width={330} className="ml-1.5 align-middle">
          <p>
            A month in which the client logged no time at all, on any agreement, draws this from
            the bank for the upkeep of the shared support systems. Any time logged for the client
            that month means no fee.
          </p>
          <p className="mt-2">
            Only a client with hours <strong>left</strong> is charged: nobody at zero, in
            overage, or whose hours have expired. The draw is capped by the balance, so a fee
            can never create an overage.
          </p>
          <p className="mt-2">
            It is charged once a month has fully elapsed, by the daily support job, and it
            appears on the statement below. Reversing one writes a compensating entry — the fee
            row stays.
          </p>
        </InfoHint>
      </p>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-grey-200 bg-white px-3 py-2">
        <label className="flex items-center gap-2 text-body-sm text-secondary">
          <input
            type="number"
            step="0.25"
            min="0"
            max="40"
            value={hours}
            disabled={exempt}
            onChange={(e) => setHours(e.target.value)}
            onBlur={commit}
            className="num w-20 rounded-md border border-grey-300 px-2 py-1 text-right text-body-sm text-secondary disabled:bg-grey-100 disabled:text-grey-400"
            aria-label={`Minimum management fee for ${bank.clientName}, hours per month`}
          />
          <span className="text-grey-500">h / month</span>
        </label>
        <label className="flex items-center gap-1.5 text-body-sm text-secondary">
          <input
            type="checkbox"
            checked={exempt}
            onChange={(e) => {
              setExempt(e.target.checked);
            }}
            className="h-4 w-4 rounded border-grey-300"
          />
          Exempt
        </label>
        {dirty && (
          <Button size="sm" loading={save.isPending} disabled={!valid} onClick={commit}>
            Save
          </Button>
        )}
        <span className="ml-auto flex flex-wrap items-center gap-x-3 text-caption text-grey-500">
          {rule.source === 'default' && orgDefault !== null && (
            <span className="num">org default {formatHours(orgDefault, { unit: true })}</span>
          )}
          {rule.source === 'client' && !rule.exempt && orgDefault !== null && (
            <span className="num">overrides the {formatHours(orgDefault, { unit: true })} default</span>
          )}
          {!bank.isActive && (
            <span className="font-semibold text-grey-600">Inactive client — not charged</span>
          )}
          <span className="num">
            {bank.managementFeeCharged > 0
              ? `${formatHours(bank.managementFeeCharged, { unit: true })} drawn to date`
              : 'nothing drawn yet'}
          </span>
        </span>
      </div>
    </div>
  );
}

/**
 * One client's bank, under the row that summarises it: what they bought, what
 * is left of each block, and the statement that proves the balance. The
 * statement is the ledger — it is not a second opinion, it is the same
 * computation the balance above it came from.
 */
export function BankPanel({
  clientId,
  showUnbanked = true,
}: {
  clientId: string;
  /** Name hours sold on an invoice that never reached this bank. Off where the page already does (the client Support card). */
  showUnbanked?: boolean;
}) {
  const { data, isLoading, isError, refetch } = useSupportBank(clientId);
  const reverseFee = useReverseManagementFee();

  if (isLoading) {
    return (
      <div className="space-y-2 p-4" aria-busy>
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }
  if (isError || !data) {
    return <ErrorState title="Couldn't load this bank" onRetry={() => refetch()} />;
  }
  if (data.blocks.length === 0) {
    return (
      <p className="px-4 py-3 text-body-sm text-grey-500">
        No hours bought yet — this client is on a monthly allowance only.
      </p>
    );
  }

  // A fee that has already been reversed offers no second Reverse button.
  const reversed = new Set(
    data.ledger
      .filter((r) => r.reason === 'management_fee_reversal' && r.periodKey)
      .map((r) => r.periodKey as string),
  );

  return (
    <div className="space-y-4 p-3 sm:p-4">
      {showUnbanked && (data.unbankedSales ?? []).length > 0 && (
        <UnbankedSales clientId={clientId} sales={data.unbankedSales} overage={data.overage} />
      )}
      {/* The client Support card already prints this beside its balance. */}
      {showUnbanked && <UnpaidBankHours unpaid={data.unpaidInvoices ?? []} />}
      {data.alerts.filter((a) => !a.acknowledgedAt).length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-status-partial-border bg-status-partial-bg px-3 py-2 text-body-sm text-secondary">
          <AlertTriangle className="h-4 w-4 shrink-0 text-warning" aria-hidden />
          {data.alerts
            .filter((a) => !a.acknowledgedAt)
            .slice(0, 3)
            .map((a) => (
              <span key={a.id} className="num">
                {thresholdLabel(a.threshold)} · {formatDate(a.sentAt.slice(0, 10))}
              </span>
            ))}
          <span className="text-caption text-grey-600">
            Recorded in-app — no email has been sent.
          </span>
        </div>
      )}

      <div>
        <p className="mb-2 text-caption font-bold uppercase tracking-wider text-grey-500">
          Blocks purchased
        </p>
        <div className="space-y-2">
          {data.blocks.map((b) => {
            const pill = BLOCK_PILL[b.status];
            const pctLeft = b.hours > 0 ? b.remaining / b.hours : 0;
            return (
              <div
                key={b.id}
                className="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-lg border border-grey-200 bg-white px-3 py-2"
              >
                <span className="num text-body-sm font-semibold text-secondary">
                  {formatHours(b.hours, { unit: true })}
                </span>
                <span className="num text-caption text-grey-500">
                  @ {formatMoney(b.rate)}/h · {formatMoney(b.amount)}
                </span>
                <span className="text-caption text-grey-500">
                  bought {formatDate(b.purchasedAt)}
                </span>
                {/* Which project the hours belong to. A block with none sits in
                    the client's pool — said in words, because Wise Software's
                    paid hours showed as attached to nothing and nobody could
                    tell whether that was a fact or a bug. */}
                {b.agreementId ? (
                  <Link
                    to={`/agreements/${b.agreementId}`}
                    className="max-w-[16rem] truncate text-caption font-semibold text-secondary hover:underline"
                    title={b.agreementName ?? undefined}
                  >
                    {b.agreementName ?? 'Agreement'}
                  </Link>
                ) : (
                  <span className="text-caption text-grey-400">client pool · no agreement</span>
                )}
                {b.expiresAt && (
                  <span
                    className={cn(
                      'text-caption',
                      b.status === 'EXPIRED'
                        ? 'text-grey-500'
                        : (b.expiresInDays ?? 99) <= 30
                          ? 'font-semibold text-warning'
                          : 'text-grey-500',
                    )}
                  >
                    expires {formatDate(b.expiresAt)}
                    {b.status === 'ACTIVE' && ` (${relativeDays(b.expiresInDays)})`}
                  </span>
                )}
                {b.invoiceId && (
                  <Link
                    to={`/invoices/${b.invoiceId}`}
                    className="text-caption font-semibold text-accent hover:underline"
                  >
                    Invoice {b.invoiceNumber ?? ''}
                    {b.invoiceState === 'OPEN' || b.invoiceState === 'PARTIAL' ? (
                      // The hours count; the money is still owed — said in words.
                      <span className="text-warning"> · not paid yet</span>
                    ) : b.invoiceState === 'DRAFT' ? (
                      ' · draft, not sent'
                    ) : b.invoiceState && b.invoiceState !== 'PAID' ? (
                      ` · ${b.invoiceState}`
                    ) : (
                      ''
                    )}
                  </Link>
                )}
                <span className="ml-auto flex items-center gap-3">
                  <span className="num text-caption text-grey-500">
                    {formatHours(b.remaining)} left
                  </span>
                  <span className="hidden h-1.5 w-20 overflow-hidden rounded-full bg-grey-200 sm:inline-flex">
                    <span
                      className={cn(
                        'h-full rounded-full',
                        b.status === 'ACTIVE' ? 'bg-success' : 'bg-grey-400',
                      )}
                      style={{ width: `${Math.max(0, Math.min(1, pctLeft)) * 100}%` }}
                    />
                  </span>
                  <StatusPill status={pill.status} label={pill.label} />
                  {/* No refund and no remove here (Jesse, 2026-08-21: "there is
                      no 'refunding' hours... adjustments... should be done in
                      the agreements not in the report"). A block mirrors a
                      Harvest Support agreement's budget - adjust or archive the
                      agreement and the sync moves the bank. */}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <ManagementFeeRow bank={data} />

      <div>
        <p className="mb-2 text-caption font-bold uppercase tracking-wider text-grey-500">
          Statement
          <span className="ml-2 font-normal normal-case tracking-normal text-grey-500">
            derived from the time entries — a balance is never a stored number
          </span>
        </p>
        <div className="overflow-hidden rounded-lg border border-grey-200 bg-white">
          <table className="w-full border-collapse text-body-sm">
            <tbody>
              {data.ledger.slice(0, 8).map((r, i) => (
                <tr key={`${r.occurredAt}-${i}`} className="border-b border-grey-100 last:border-b-0">
                  <td className="whitespace-nowrap px-3 py-1.5 text-grey-600">
                    {formatDate(r.occurredAt)}
                  </td>
                  <td className="px-3 py-1.5 text-secondary">
                    {REASON_LABEL[r.reason]}
                    {r.note && <span className="ml-1.5 text-caption text-grey-500">{r.note}</span>}
                    {/* The only way to undo a fee, and it must not shout: six
                        ghost BUTTONS in an eight-row statement read as a toolbar.
                        A link is the right weight for a rare, reversible act. */}
                    {r.reason === 'management_fee' && r.periodKey && !reversed.has(r.periodKey) && (
                      <button
                        type="button"
                        className="ml-2 text-caption font-semibold text-accent hover:underline disabled:text-grey-400"
                        disabled={reverseFee.isPending}
                        onClick={() =>
                          reverseFee
                            .mutateAsync({ clientId, period: r.periodKey as string })
                            .then(() => toast.success('Management fee reversed.'))
                            .catch((err: unknown) =>
                              toast.error(
                                err instanceof Error ? err.message : 'That did not work.',
                              ),
                            )
                        }
                      >
                        Reverse
                      </button>
                    )}
                  </td>
                  <td
                    className={cn(
                      'num-cell whitespace-nowrap px-3 py-1.5 text-right font-semibold',
                      r.delta < 0 ? 'text-secondary-800' : 'text-success',
                      r.reason === 'overage' && 'text-status-late',
                    )}
                  >
                    {r.delta > 0 ? '+' : ''}
                    {formatHours(r.delta)}
                  </td>
                  <td className="num-cell whitespace-nowrap px-3 py-1.5 text-right text-grey-500">
                    {/* The running total floors at 0 on screen (Jesse,
                        2026-08-20): a Harvest history can push it hundreds of
                        hours negative, and a negative balance reads as hours we
                        owe. The overage rows above already carry the excess. */}
                    {formatHours(Math.max(0, r.balanceAfter), { unit: true })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.ledger.length > 8 && (
          <p className="mt-1.5 text-caption text-grey-500">
            Showing the last 8 of {data.ledger.length} movements.
          </p>
        )}
      </div>

    </div>
  );
}
